/* eslint-disable eslint-comments/disable-enable-pair -- need flat button */

import { useIsMutating } from "@tanstack/react-query";
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Notification } from "@tql/ui-components-react/kendo/notification";
import { lockUnlocked_01 } from "@tql/ui-components-react/untitled/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { environmentConfig } from "~/shared/utils/environment-config.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import { ErrorModal } from "../components/error-modal/index.tsx";
import { Exception } from "../components/exception/exception.tsx";
import { Remarks } from "../components/remarks/remarks.tsx";
import { ShipmentDetails } from "../components/shipment-details/shipment-details.tsx";
import { ShipmentProgress } from "../components/shipment-progress/shipment-progress.tsx";
import { TrackingHistory } from "../components/tracking-history/tracking-history.tsx";
import { useRestoreShipment } from "../hooks/use-restore-shipment.ts";
import { useShipmentEditSession } from "../hooks/use-shipment-edit-session.ts";
import {
	useForceUnlockShipment,
	useShipmentLockState,
} from "../hooks/use-shipment-lock.ts";
import { useShipmentManagementEffects } from "../hooks/use-shipment-management-effects.ts";
import type { AugmentedShipment } from "../types/augmented-shipment";
import { formatSpecialInstructions } from "../utils/format-special-instructions.ts";
import { getStatusLockLabel } from "../utils/get-status-lock-label.ts";
import { isQuoteExpired } from "../utils/is-quote-expired.ts";
import type { ActiveModal } from "./shipment-action-modals.tsx";
import { ShipmentActionModals } from "./shipment-action-modals.tsx";
import { ShipmentHeader } from "./shipment-header.tsx";

type MenuItem = {
	label: string;
	lockable: boolean;
	onClick: () => void;
	dividerBefore?: boolean;
};

const VOIDED_ENABLED_ITEMS = new Set([
	"Restore Shipment",
	"Copy Quote",
	"Copy Shipment",
]);

const STATUS_LOCK_ENABLED_ITEMS = new Set([
	"Additional Details",
	"MLS Portal",
	"Claims",
	"Copy Quote",
	"Copy Shipment",
]);

export const InternalShipmentManagement = ({
	shipment,
	handleDocumentClick,
	error,
}: {
	shipment: AugmentedShipment;
	handleDocumentClick: () => void;
	error: Error | null;
}) => {
	const [activeModal, setActiveModal] = useState<ActiveModal>(null);
	const closeModal = useCallback(() => setActiveModal(null), []);
	const openSaveChangesModal = useCallback(
		() => setActiveModal({ type: "saveChanges" }),
		[],
	);
	const params = useParams();
	const navigate = useNavigate();
	const poNumber = params["poNumber"] ?? "";
	const { employeeId } = useIsAuthenticated();
	const {
		isLockedByOther: isLockedByOtherState,
		isForcingUnlock,
		wasForceUnlocked,
	} = useShipmentLockState();
	const lock = shipment?.loadAccess?.lock;

	const lockedBySelf =
		lock != null && String(lock.lockedByUserId) === employeeId;
	const isLockedByOther = isLockedByOtherState && !lockedBySelf;
	const isVoided = shipment?.loadAccess?.statuses?.includes("Voided") ?? false;
	const statusLockLabel = getStatusLockLabel(shipment);
	const isStatusLocked = statusLockLabel !== null;

	const isReadOnly = isLockedByOther || isVoided || isStatusLocked;
	const { restoreShipment, isRestoringShipment } = useRestoreShipment();
	const forceUnlock = useForceUnlockShipment(poNumber);
	const {
		discardAndLeave,
		exitEditMode,
		handleSave,
		hasUnsavedChanges,
		isEditing,
		isCreatingRerate,
		isRefetchingAfterSave,
		isUpdatingRerate,
		onRerate,
		rerateRequired,
		stayOnPage,
		toggleEditing,
	} = useShipmentEditSession({
		shipment,
		onNavigationBlocked: openSaveChangesModal,
		onNavigationResolved: closeModal,
	});

	// A status lock can land mid-edit (e.g. Send to Accounting), after which the draft can't be saved.
	useEffect(() => {
		if (isStatusLocked && isEditing) {
			exitEditMode();
		}
	}, [exitEditMode, isEditing, isStatusLocked]);

	// Cancelling an in-progress edit needs confirmation only if something
	// actually changed; entering edit mode never does.
	const handleToggleEditing = useCallback(() => {
		if (isEditing) {
			if (hasUnsavedChanges()) {
				setActiveModal({ type: "discardEdits" });
			} else {
				toggleEditing();
			}
			return;
		}
		toggleEditing();
	}, [hasUnsavedChanges, isEditing, toggleEditing]);

	const handleDiscardEdits = useCallback(() => {
		exitEditMode();
		closeModal();
	}, [closeModal, exitEditMode]);

	const isCancelling =
		useIsMutating({ mutationKey: ["voidShipment"], exact: true }) > 0;

	useShipmentManagementEffects({
		poNumber: Number(poNumber),
		wasForceUnlocked,
		setActiveModal,
	});

	const isCancellingDispatch =
		useIsMutating({ mutationKey: ["cancelDispatch"], exact: true }) > 0;

	const isSendingAccounting =
		useIsMutating({ mutationKey: ["sendAccounting"], exact: true }) > 0;

	const openMlsPortal = useCallback(() => {
		const mlsEnvPrefix =
			{ local: "dev", dev: "dev", stage: "stage" }[
				environmentConfig.environmentName ?? ""
			] ?? "";
		if (shipment.isMLS) {
			window.open(
				`https://mls${mlsEnvPrefix}.tql.com/multiLegShipments/${poNumber}`,
				"_blank",
				"noopener,noreferrer",
			);
			return;
		}
		window.open(
			`https://mls${mlsEnvPrefix}.tql.com`,
			"_blank",
			"noopener,noreferrer",
		);
	}, [poNumber, shipment.isMLS]);

	const navigateToCopy = useCallback(
		(route: string, state: { isCopy?: boolean; isCopyQuote?: boolean }) => {
			navigate(route.replace(":quoteId", String(shipment.quoteId ?? 0)), {
				state,
			});
		},
		[navigate, shipment.quoteId],
	);

	// quoteRecall.specialInstructions is a snapshot from when the quote was
	// first created, so it goes stale once the user edits Special Instructions
	// on the Shipment Details screen (which only updates
	// shipmentDetails.pickup/delivery.specialInstructions). Copy Quote/Shipment
	// should carry over what the user currently sees, not the stale snapshot.
	const quoteRecallForCopy = useMemo(() => {
		if (!shipment.quoteRecall) return undefined;
		return {
			...shipment.quoteRecall,
			specialInstructions: formatSpecialInstructions(
				shipment.shipmentDetails.pickup?.specialInstructions,
				shipment.shipmentDetails.delivery?.specialInstructions,
			),
		};
	}, [shipment.quoteRecall, shipment.shipmentDetails]);

	// Copy Quote starts a fresh quote populated from the shipment's quoteRecall
	// payload (no quoteId in the URL). Falls back to the id-based navigation when
	// quoteRecall is unavailable.
	const navigateToCopyQuote = useCallback(() => {
		if (quoteRecallForCopy) {
			navigate(routes.quoteDetails, {
				state: {
					isCopyQuote: true,
					quoteRecall: quoteRecallForCopy,
					copyQuoteCustomer: shipment.customer,
				},
			});
			return;
		}
		navigateToCopy(routes.quoteDetailsWithId, { isCopyQuote: true });
	}, [navigate, navigateToCopy, shipment.customer, quoteRecallForCopy]);

	// Copy Shipment starts a fresh LTL shipment populated from the shipment's
	// quoteRecall payload (no quoteId in the URL), mirroring Copy Quote. Falls
	// back to the id-based navigation when quoteRecall is unavailable.
	const navigateToCopyShipment = useCallback(() => {
		if (quoteRecallForCopy) {
			navigate(routes.ltlShipment, {
				state: {
					isCopy: true,
					quoteRecall: quoteRecallForCopy,
					copyQuoteCustomer: shipment.customer,
				},
			});
			return;
		}
		navigateToCopy(routes.ltlShipmentWithId, { isCopy: true });
	}, [navigate, navigateToCopy, shipment.customer, quoteRecallForCopy]);

	const dropDownList = useMemo(() => {
		const hasNoCommodities =
			(shipment.shipmentDetails.freightDetails.lineItems ?? []).length === 0;
		const menuItems: MenuItem[] = [
			{
				label: "Additional Details",
				lockable: true,
				onClick: () => setActiveModal({ type: "additionalDetails" }),
			},
			{
				label: "Attention Flag",
				lockable: true,
				onClick: () => setActiveModal({ type: "createFlag" }),
			},
			{ label: "Rerate", lockable: true, onClick: onRerate },
			{ label: "MLS Portal", lockable: true, onClick: openMlsPortal },
			{
				label: "Claims",
				lockable: true,
				onClick: () => setActiveModal({ type: "claims" }),
			},
			isVoided
				? {
						label: "Restore Shipment",
						lockable: true,
						onClick: () => restoreShipment(Number(poNumber)),
					}
				: {
						label: "Cancel Shipment",
						lockable: true,
						onClick: () => setActiveModal({ type: "cancelShipment" }),
					},
			{
				label: "Copy Quote",
				lockable: false,
				dividerBefore: true,
				onClick: navigateToCopyQuote,
			},
			{
				label: "Copy Shipment",
				lockable: false,
				onClick: navigateToCopyShipment,
			},
		];

		return menuItems.map((item) => ({
			label: item.label,
			dividerBefore: item.dividerBefore ?? false,
			disabled: isVoided
				? !VOIDED_ENABLED_ITEMS.has(item.label)
				: (isStatusLocked && !STATUS_LOCK_ENABLED_ITEMS.has(item.label)) ||
					(item.lockable && isLockedByOther) ||
					(item.label === "Rerate" && hasNoCommodities),
			onClick: item.onClick,
		}));
	}, [
		isLockedByOther,
		isStatusLocked,
		isVoided,
		navigateToCopyQuote,
		navigateToCopyShipment,
		onRerate,
		openMlsPortal,
		poNumber,
		restoreShipment,
		shipment.shipmentDetails.freightDetails.lineItems,
	]);

	return (
		<div
			className={isStatusLocked ? "container status-locked" : "container"}
			data-testid={"shipment-management-container"}
		>
			<LoadingSpinner
				isVisible={
					isCreatingRerate ||
					isUpdatingRerate ||
					isRefetchingAfterSave ||
					isForcingUnlock ||
					isCancelling ||
					isCancellingDispatch ||
					isRestoringShipment ||
					isSendingAccounting
				}
			/>
			<ErrorModal error={error} show={!!error} />
			<ShipmentActionModals
				activeModal={activeModal}
				forceUnlock={() => void forceUnlock()}
				isSaving={isUpdatingRerate}
				onClose={closeModal}
				onDiscardAndLeave={discardAndLeave}
				onDiscardEdits={handleDiscardEdits}
				onSaveAndLeave={() => void handleSave({ leaveAfterSave: true })}
				onStayOnPage={stayOnPage}
				rerateRequired={rerateRequired}
				shipment={shipment}
			/>
			<ShipmentHeader
				customerName={error ? "" : (shipment?.customer?.customerName ?? "")}
				dropDownList={dropDownList}
				isEditing={isEditing}
				isLockedByOther={isLockedByOther}
				isQuoteExpired={!isVoided && isQuoteExpired(shipment)}
				isVoided={isVoided}
				lock={lock ?? null}
				onDocumentClick={handleDocumentClick}
				onForceUnlock={() => setActiveModal({ type: "forceUnlock" })}
				onSave={() => void handleSave()}
				onToggleEditing={handleToggleEditing}
				poNumber={error ? "" : poNumber}
				rerateRequired={rerateRequired}
				statusLockLabel={statusLockLabel}
			/>

			{isCancelling ? (
				<div className={"cancellation-banner"}>
					<Notification type={{ style: "info", icon: false }}>
						Cancelling shipment. This may take a few moments.
					</Notification>
				</div>
			) : null}

			<fieldset
				className={
					isReadOnly ? "editable-region read-only-region" : "editable-region"
				}
				data-testid={"editable-region"}
				disabled={isReadOnly}
			>
				<div className={"exception-grid"}>
					{wasForceUnlocked ? (
						<Notification type={{ style: "info", icon: false }}>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "8px",
								}}
							>
								<SvgIcon icon={lockUnlocked_01} size={"medium"} />
								<span>This shipment has been unlocked.</span>
							</div>
						</Notification>
					) : null}
					<Exception
						exceptionCount={shipment?.immediateActionExceptions?.length ?? 0}
						message={"Action type exception"}
						onClick={() =>
							setActiveModal({
								type: "exception",
								exceptionType: "immediateActionExceptions",
							})
						}
						type={"immediateActionExceptions"}
					/>
					<Exception
						exceptionCount={shipment?.needsAcknowledgedExceptions?.length ?? 0}
						message={"Acknowledge type exception"}
						onClick={() =>
							setActiveModal({
								type: "exception",
								exceptionType: "needsAcknowledgedExceptions",
							})
						}
						type={"needsAcknowledgedExceptions"}
					/>
				</div>
				<div className={"shipment-management-grid"}>
					<div className={"left-column"}>
						<ShipmentProgress
							details={shipment?.shipmentProgress}
							shipment={shipment}
						/>
						<TrackingHistory
							carrierWebsite={shipment?.carrier?.carrierWebsite}
							details={shipment?.groupedCheckCalls ?? {}}
						/>
						<Remarks details={shipment?.groupedRemarks ?? {}} />
					</div>
					<div className={"right-column"}>
						<ShipmentDetails onRerate={onRerate} shipment={shipment} />
					</div>
				</div>
			</fieldset>
		</div>
	);
};
